import React, { useEffect, useMemo, useState } from 'react';
import Buttons from '../../Atoms/Buttons/Buttons';
import styles from './SettingsPage.module.css';
import searchIcon from '../../../assets/Imagess/searchicon.svg';
import SearchInput from '../../Atoms/Inputs/SearchInput';
import plus from '../../../assets/Imagess/plusvector.svg';
import moreicon from '../../../assets/Imagess/moreicon.svg';
import SettingsModal from '../../Molecules/Settings/SettingsModal/SettingsModal';
import DeletePopup from '../../Molecules/Custom/DeletePopup';
import MoreButton from '../../Molecules/Custom/MoreButton';
import exportICon from '../../../assets/Imagess/exportIcon.svg';
import printicon from '../../../assets/Imagess/printIcon.svg';
import CustomizedSelect from '../../Atoms/Inputs/CustomizedSelect';
import {
  useDeleteSettingsOption,
  useDeleteTerm,
  useExportDropDownOptionsTable,
  useFetchDropDownTypes,
  useFetchRelatedToType,
  useFetchPositions,
  useDeletePosition,
} from '../../../Hooks/Settings';
import { toast } from 'react-toastify';
import TableComponent from '../../Molecules/Settings/TableComponent/TableComponent';
import * as tableHeader from '../../Molecules/Settings/TableComponent/HeaderSettingTable.jsx/SettingsHeader';
import { useToast } from '../../Molecules/Custom/CustomToaster/Toast';
import PositionsModal from '../../Molecules/Settings/SettingsModal/PositionsModal';

const POSITIONS_KEY = 'positions';

const SettingsPage = ({
  title,
  header,
  data,
  type,
  refetchTypes,
  isRelated,
  isOptionsLoading,
  isSinglular,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [deletMode, setDeleteMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});
  const [selectedValueText, setSelectedValueText] = useState('');
  const [search, setSearch] = useState('');
  const customizedToast = useToast();

  const isPositions = type === POSITIONS_KEY;
  const showRelatedSelect = Boolean(isRelated) && !isPositions;

  const { data: relatedOptions } = useFetchDropDownTypes(isRelated, {
    enabled: showRelatedSelect,
  });

  const {
    data: parentOptions,
    refetch: refetchParentOptions,
    isLoading: isOptionsParentLoading,
  } = useFetchRelatedToType(
    { type, parentId: selectedValueText },
    { enabled: showRelatedSelect && Boolean(selectedValueText) }
  );
  const { mutate: deletePosition, isLoading: isDeletePosition } =
    useDeletePosition();
  const {
    data: positionsRes,
    refetch: refetchPositions,
    isLoading: isPositionsLoading,
  } = useFetchPositions({ enabled: isPositions });

  const { isLoading: isDeleteTerm } = useDeleteTerm();
  const { mutate: deleteSettingsOption, isLoading: isDeleteSettings } =
    useDeleteSettingsOption();

  const effectiveRefetch = isPositions ? refetchPositions : refetchTypes;

  const handleAddClick = () => {
    if (showRelatedSelect && !selectedValueText) {
      toast.error(`Choose a country before adding a ${title}`);
      return;
    }
    setSelectedItem({});
    setIsModalOpen(true);
    setModalMode('add');
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
    setModalMode('edit');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem({});
  };
  const handleDelete = () => {
    if (isPositions) {
      const id = selectedItem?.positionId || selectedItem?.value;

      if (!id) {
        toast.error('No position id found');
        return;
      }

      deletePosition(id, {
        onSuccess: () => {
          setDeleteMode(false);
          setSelectedItem({});
          refetchPositions?.();
          customizedToast.open('تم الحذف بنجاح');
        },
        onError: (error) => {
          console.error('Error deleting position', error);
          toast.error('Error');
          setDeleteMode(false);
          setSelectedItem({});
        },
      });

      return;
    }

    deleteSettingsOption(selectedItem?.value, {
      onSuccess: () => {
        setDeleteMode(false);
        setSelectedItem({});
        effectiveRefetch?.();
        customizedToast.open('تم الحذف بنجاح');
        refetchParentOptions?.();
      },
      onError: (error) => {
        console.error('Error deleting option', error);
        toast.error('Error');
        setDeleteMode(false);
        setSelectedItem({});
      },
    });
  };

  const handleExportDropDownOptions = async () => {
    if (isPositions) {
      toast.error('تصدير المناصب يحتاج Endpoint خاص بالمناصب');
      return;
    }

    try {
      const res = await useExportDropDownOptionsTable.mutateAsync();

      if (res.data instanceof Blob) {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `${title}Table.xlsx`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      } else {
        console.error('Unexpected data type. Expected Blob.');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  useEffect(() => {
    setSearch('');
    setSelectedValueText('');
  }, [type]);
  const positionsAsDropdownItems = useMemo(() => {
    const list = Array.isArray(positionsRes?.data) ? positionsRes.data : [];
    return list.map((p) => ({
      id: p.positionId,
      value: p.positionId,
      text_EN: p.positionEN,
      text_AR: p.positionAR,

      positionId: p.positionId,
      positionEN: p.positionEN,
      positionAR: p.positionAR,
    }));
  }, [positionsRes]);

  const normalDropdownItems = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    return list.map((item) => ({
      ...item,
      id: item.value,
    }));
  }, [data]);

  const tableItems = showRelatedSelect
    ? (parentOptions?.data || []).map((item) => ({ ...item, id: item.value }))
    : isPositions
    ? positionsAsDropdownItems
    : normalDropdownItems;

  const tableData = {
    data: { items: tableItems },
    refetch: effectiveRefetch,
    isLoading:
      (isPositions ? isPositionsLoading : isOptionsLoading) ||
      isOptionsParentLoading,
    tableHeader: tableHeader.settingsTableHeader,
  };

  return (
    <>
      <div className={styles['settings-cont']}>
        <div className={styles['icon-container']}>
          <SearchInput
            icon={searchIcon}
            search={search}
            setSearch={setSearch}
          />

          <div className={styles['icon-subcontainer']}>
            <Buttons
              text={title}
              icon={plus}
              rounded={true}
              color='#0DA06E'
              handleOnClick={handleAddClick}
            />

            <MoreButton icon={moreicon} left='0' width={'249px'}>
              <div className='profile-container'>
                <div onClick={handleExportDropDownOptions}>
                  <img src={exportICon} alt='profile' /> <h4>تصدير</h4>
                </div>
                <div>
                  <img src={printicon} alt='profile' /> <h4>طباعة</h4>
                </div>
              </div>
            </MoreButton>
          </div>
        </div>

        <div className={styles['select-container']}>
          {showRelatedSelect && (
            <CustomizedSelect
              label='اختر البلد'
              options={(relatedOptions?.data || []).map((option) => ({
                value: option.value,
                text: option.text_EN,
              }))}
              placeholder='اختر بلدًا'
              value={selectedValueText}
              onChange={setSelectedValueText}
            />
          )}
        </div>

        <TableComponent
          tableData={tableData}
          editAdd
          handleEditClick={handleEditClick}
          handleDelete={(item) => (setDeleteMode(true), setSelectedItem(item))}
        />
      </div>

      {isModalOpen && type !== 16 ? (
        isPositions ? (
          <PositionsModal
            mode={modalMode}
            selectedItem={selectedItem}
            handleOnClose={handleCloseModal}
            refetch={refetchPositions}
          />
        ) : (
          <SettingsModal
            title={title}
            label1={title + ' ' + 'ID'}
            handleOnClose={handleCloseModal}
            mode={modalMode}
            type={type}
            refetchTypes={refetchTypes}
            selectedValueText={selectedValueText}
            refetchParentOptions={refetchParentOptions}
            isRelated={isRelated}
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
          />
        )
      ) : (
        ''
      )}

      {deletMode && (
        <DeletePopup
          handleOnClose={() => (setDeleteMode(false), setSelectedItem({}))}
          title={title}
          handleDelete={handleDelete}
          isLoading={isDeleteTerm || isDeleteSettings}
        />
      )}
    </>
  );
};

export default SettingsPage;
